import { InfoOutlined } from '@mui/icons-material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Box, Typography } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';

interface ProfileSection {
    label: string;
    completed: number;
}

interface CompanyProfileCompletionProps {
    pro: any;
}

const CompletionContainer = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.dashboard.main,
    borderRadius: '16px',
    padding: '24px 16px',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
}));

const ProgressBarContainer = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.action.disabledBackground,
    height: '16px',
    borderRadius: '12px',
    position: 'relative',
    overflow: 'hidden',
}));

const ProgressFill = styled(Box)<{ progress: number }>(({ progress }) => ({
    height: '16px',
    borderRadius: '8px',
    width: `${progress}%`,
    position: 'absolute',
    left: 0,
    top: 0,
    transition: 'width 0.3s ease',
    zIndex: 2,
}));

const ProgressText = styled(Typography)(() => ({
    fontFamily: 'Plus Jakarta Sans, sans-serif',
    fontWeight: 500,
    fontSize: '12px',
    color: '#fff',
    textAlign: 'center',
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    zIndex: 10,
}));

const SectionRow = styled(Box)(() => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
}));

const SectionLabel = styled(Typography)<{ completed?: boolean }>(({ completed, theme }) => ({
    fontFamily: 'Plus Jakarta Sans, sans-serif',
    fontWeight: 600,
    fontSize: '14px',
    color: completed ? theme.palette.successGreen.main : theme.palette.warningOrange.main,
    textDecoration: completed ? 'line-through' : 'none',
    textDecorationSkipInk: 'none',
}));

const IconContainer = styled(Box)(() => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '20px',
    height: '20px',
}));

const CompanyProfileCompletion = ({ pro }: CompanyProfileCompletionProps) => {
    const theme = useTheme();

    const completion = pro?.profileCompletionRate;
    if (!completion) return null;

    const rate: number = completion.rate ?? 0;

    // Hide widget when profile is fully complete
    if (rate >= 100) return null;

    const sections: ProfileSection[] = [
        { label: 'Votre société', completed: completion.sections?.company?.completed ?? 0 },
        { label: 'Votre identité', completed: completion.sections?.identity?.completed ?? 0 },
        { label: 'Coordonnées', completed: completion.sections?.contact?.completed ?? 0 },
        { label: 'À propos de ma société', completed: completion.sections?.about?.completed ?? 0 },
        { label: 'Géolocalisation', completed: completion.sections?.geolocation?.completed ?? 0 },
        { label: 'Nos Galeries', completed: completion.sections?.galleries?.completed ?? 0 },
        { label: 'Vidéo de présentation', completed: completion.sections?.video?.completed ?? 0 },
    ];

    const barColor =
        rate >= 80
            ? theme.palette.successGreen.main
            : rate >= 50
              ? theme.palette.warningOrange.main
              : theme.palette.errorRed.main;

    return (
        <CompletionContainer>
            {/* Header Section */}
            <Box display="flex" flexDirection="column" gap="4px">
                <Typography
                    variant="h6"
                    sx={{
                        fontSize: '24px',
                        fontWeight: 500,
                        lineHeight: 'normal',
                    }}
                >
                    Complétion du profil
                </Typography>
                <Typography
                    sx={{
                        fontFamily: 'Plus Jakarta Sans, sans-serif',
                        fontWeight: 400,
                        fontSize: '14px',
                        color: theme.palette.text.secondary,
                    }}
                >
                    Complétez votre profil pour améliorer votre visibilité
                </Typography>
            </Box>

            {/* Progress Bar */}
            <ProgressBarContainer>
                <ProgressFill progress={rate} sx={{ backgroundColor: barColor }} />
                <ProgressText>{rate}%</ProgressText>
            </ProgressBarContainer>

            {/* Sections List */}
            <Box display="flex" flexDirection="column" gap="12px">
                {sections.map((section) => {
                    const isDone = section.completed >= 100;
                    return (
                        <SectionRow key={section.label}>
                            <SectionLabel completed={isDone}>
                                {section.label}
                            </SectionLabel>
                            <IconContainer>
                                {isDone ? (
                                    <CheckCircleIcon
                                        sx={{
                                            color: theme.palette.successGreen.main,
                                            fontSize: '20px',
                                        }}
                                    />
                                ) : (
                                    <InfoOutlined
                                        sx={{
                                            color: theme.palette.warningOrange.main,
                                            fontSize: '20px',
                                        }}
                                    />
                                )}
                            </IconContainer>
                        </SectionRow>
                    );
                })}
            </Box>
        </CompletionContainer>
    );
};

export default CompanyProfileCompletion;
